import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { loadPlaid } from '@/features/plaid/loadPlaidScript'
import { plaidApi } from '@/features/plaid/api'

const LINK_TOKEN_KEY = 'plaid_link_token'

/**
 * Plaid OAuth return URL. Banks that require OAuth bounce the user here
 * with ?oauth_state_id=... after they approve the link. We resume the
 * Plaid Link flow with receivedRedirectUri so it picks up where it left
 * off, then hand back to /plaid regardless of success/exit.
 *
 * Route is intentionally outside the AppShell auth loader - if the JWT
 * expired during the bank handoff, the subsequent /api/plaid/create_link_token
 * call will 401 and api.ts will redirect to /login.
 */
export function PlaidOAuthCallbackPage() {
  const navigate = useNavigate()
  const [status, setStatus] = useState('Completing bank connection…')

  useEffect(() => {
    let cancelled = false

    const resume = async () => {
      try {
        const Plaid = await loadPlaid()
        let linkToken = localStorage.getItem(LINK_TOKEN_KEY)
        if (!linkToken) {
          const { link_token } = await plaidApi.createLinkToken()
          linkToken = link_token
        }
        if (cancelled) return
        const handler = Plaid.create({
          token: linkToken,
          receivedRedirectUri: window.location.href,
          onSuccess: () => {
            localStorage.removeItem(LINK_TOKEN_KEY)
            navigate('/plaid', { replace: true })
          },
          onExit: () => {
            localStorage.removeItem(LINK_TOKEN_KEY)
            navigate('/plaid', { replace: true })
          },
        })
        handler.open()
      } catch {
        if (cancelled) return
        setStatus('Connection failed. Redirecting…')
        setTimeout(() => navigate('/plaid', { replace: true }), 2000)
      }
    }

    void resume()
    return () => {
      cancelled = true
    }
  }, [navigate])

  return (
    <div className="flex min-h-dvh items-center justify-center bg-background">
      <div className="text-center">
        <div className="mx-auto mb-4 size-12 animate-spin rounded-full border-4 border-muted border-t-primary" />
        <p className="text-sm text-muted-foreground">{status}</p>
      </div>
    </div>
  )
}
